// src/pages/admin/WorkerUpdate.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Container,
  Form as RSForm,
  Input,
  Label,
  FormFeedback,
  Alert,
  Button,
  Collapse
} from "reactstrap";
import { useFormik } from "formik";
import "react-phone-input-2/lib/bootstrap.css";
import toastr from "toastr";
import withRouter from "../../../components/Common/withRouter";
import { withTranslation } from "react-i18next";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";
import FormPhoneInput from "../../../components/Common/FormPhoneInput";

// APIs
import { useUserMe, useUpdateWorkerUser } from "../../../api/users";
import { useGetAllSectorsNotPaginated } from "../../../api/sector";
import { useGetAllCertsNotPaginated } from "../../../api/certs";
import { mediaUrl } from "../../../helpers/mediaHelper";
import { toSentenceCase } from "../../../utils/stringUtils";
import {
  getNationalityOptions,
  AVAILABILITY_OPTIONS,
  getLanguageOptions,
  TRANSPORT_OPTIONS,
  WHAT_MATTERS_MOST_OPTIONS,
  normalizeWhatMattersMost,
  PREFERRED_WORK_RADIUS_OPTIONS,
  FILE_VALIDATION,
  GENDER_OPTIONS,
  RIGHT_TO_WORK_STATUS_BRITISH_IRISH,
  RIGHT_TO_WORK_STATUS_OPTIONS_ALL,
  EMERGENCY_RELATIONSHIP_OPTIONS,
  STARTER_CHECKLIST_OPTIONS,
  STUDENT_LOAN_PLAN_OPTIONS,
  UK_COUNTRY,
  UK_DVLA_SHARE_CODE_URL,
  UK_DBS_UPDATE_SERVICE_URL,
} from "../../../constants/workerOnboarding";
import { GOOGLE_MAP_KEY } from "../../../environtments/environtment";
import {
  buildWorkerProfileValidationSchema,
  validateCertificateEntries,
} from "./workerProfileValidation";
import { buildWorkerProfileFormData } from "./buildWorkerProfileFormData";
import { mapWorkerApiToFormValues } from "./mapWorkerApiToFormValues";
import WorkerProfileSkeleton from "../WorkerProfileSkeleton";

const isUnder = (file, mb) =>
  file && file.size <= mb * FILE_VALIDATION.BYTES_IN_MB;

function isImageMedia(value) {
  if (!value) return false;
  if (value instanceof File) {
    if (value.type?.startsWith("image/")) return true;
    return FILE_VALIDATION.ALLOWED_IMAGE_EXTENSIONS.test(value.name);
  }
  if (typeof value === "string") {
    return FILE_VALIDATION.ALLOWED_IMAGE_EXTENSIONS.test(value.split("?")[0]);
  }
  return false;
}

function FileMediaPreview({
  source,
  alt = "",
  maxHeight = 160,
  linkLabel = "View current file",
}) {
  // default kept for callers; prefer passing t("viewCurrentFile")
  const isFile = source instanceof File;
  const [objectUrl, setObjectUrl] = useState(null);

  const pathUrl = useMemo(() => {
    if (!source || isFile) return "";
    return mediaUrl(source);
  }, [source, isFile]);

  useEffect(() => {
    if (!isFile || !source) {
      setObjectUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(source);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [source, isFile]);

  const displayUrl = isFile ? objectUrl : pathUrl;
  if (!displayUrl) return null;

  if (!isImageMedia(source)) {
    return (
      <a
        href={displayUrl}
        target="_blank"
        rel="noreferrer"
        className="d-block mb-2"
      >
        {linkLabel}
      </a>
    );
  }

  return (
    <a
      href={displayUrl}
      target="_blank"
      rel="noreferrer"
      className="d-inline-block mb-2"
      style={{ lineHeight: 0 }}
    >
      <img
        src={displayUrl}
        alt={alt}
        style={{
          maxHeight,
          maxWidth: "100%",
          objectFit: "contain",
          borderRadius: 4,
          border: "1px solid #dee2e6",
          backgroundColor: "#fff",
        }}
      />
    </a>
  );
}

function splitWorkerName(firstName, surname, fullName) {
  const first = (firstName || "").trim();
  const sur = (surname || "").trim();
  if (first || sur) {
    return { firstName: first, lastName: sur };
  }
  const raw = (fullName || "").trim();
  if (!raw) return { firstName: "", lastName: "" };
  const parts = raw.split(/\s+/);
  if (parts.length >= 2) {
    return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
  }
  return { firstName: raw, lastName: "" };
}

function countryOptionsIncludingValue(options, value) {
  const v = (value || "").trim();
  if (!v || options.some((o) => o.value === v)) return options;
  return [...options, { value: v, label: v }];
}

function resolveSelectValue(options, value) {
  const v = (value || "").trim();
  if (!v) return null;
  return options.find((o) => o.value === v) || { value: v, label: v };
}

function normalizeEmergencyRelationship(value) {
  const v = (value || "").trim();
  if (!v) return "";
  if (EMERGENCY_RELATIONSHIP_OPTIONS.some((o) => o.value === v)) return v;
  const aliases = {
    sister: "Sibling",
    brother: "Sibling",
    mother: "Parent",
    father: "Parent",
    mum: "Parent",
    dad: "Parent",
    mom: "Parent",
    husband: "Spouse",
    wife: "Spouse",
    son: "Child",
    daughter: "Child",
    boyfriend: "Partner",
    girlfriend: "Partner",
  };
  return aliases[v.toLowerCase()] || v;
}

function parseGoogleAddressComponents(place) {
  if (!place?.address_components) return null;
  const comps = place.address_components;
  const get = (...types) => {
    const c = comps.find((x) => types.some((t) => x.types.includes(t)));
    return c?.long_name || "";
  };
  const streetNumber = get("street_number");
  const route = get("route");
  const line1 = [streetNumber, route].filter(Boolean).join(" ").trim();
  const subpremise = get("subpremise");
  const postcode = get("postal_code");
  const city =
    get("postal_town") || get("locality") || get("administrative_area_level_2");
  const county =
    get("administrative_area_level_2") || get("administrative_area_level_1");
  const country = get("country");
  const loc = place.geometry?.location;
  const lat =
    loc == null ? null : typeof loc.lat === "function" ? loc.lat() : loc.lat;
  const lng =
    loc == null ? null : typeof loc.lng === "function" ? loc.lng() : loc.lng;
  return {
    addressLine1: line1 || place.name || "",
    addressLine2: subpremise || "",
    postcode,
    city,
    county,
    country,
    lat:
      lat != null && typeof lat === "number"
        ? String(lat.toFixed(6))
        : lat != null
        ? String(lat)
        : "",
    lng:
      lng != null && typeof lng === "number"
        ? String(lng.toFixed(6))
        : lng != null
        ? String(lng)
        : "",
  };
}

function formatDisplayDate(value) {
  if (!value || !String(value).trim()) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function VerificationStatusBadge({ status, t }) {
  const icon =
    status === true
      ? "mdi-check-circle-outline"
      : status === false
      ? "mdi-close-circle-outline"
      : "mdi-clock-outline";
  const label =
    status === true
      ? t("verificationVerified")
      : status === false
      ? t("verificationUnverified")
      : t("verificationPendingReview");

  return (
    <span
      className={`badge rounded-pill border fw-semibold px-3 py-2 d-inline-flex align-items-center gap-1 ${
        status === true
          ? "border-success text-success bg-success bg-opacity-10"
          : status === false
          ? "border-danger text-danger bg-danger bg-opacity-10"
          : "border-secondary text-secondary bg-light"
      }`}
      style={{
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
        fontSize: "0.8125rem",
      }}
    >
      <i className={`mdi ${icon}`} aria-hidden />
      {label}
    </span>
  );
}

function AdminReviewMetaRow({
  label,
  status,
  expiryDate,
  issueDate,
  lastCheckedDate,
  t,
}) {
  const formattedExpiry = formatDisplayDate(expiryDate);
  const formattedIssue = formatDisplayDate(issueDate);
  const formattedChecked = formatDisplayDate(lastCheckedDate);
  const hasStatus = status === true || status === false;

  return (
    <div className="worker-admin-review-item p-3 rounded border bg-white h-100">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <span className="fw-bolder small text-uppercase text-muted">
          {label}
        </span>
        <VerificationStatusBadge status={status} t={t} />
      </div>
      <div className="d-flex flex-wrap gap-3 small">
        {formattedExpiry ? (
          <div>
            <span className="text-muted">{t("adminReviewExpiryDate")}: </span>
            <span className="fw-semibold">{formattedExpiry}</span>
          </div>
        ) : hasStatus ? (
          <span className="text-muted">{t("adminReviewExpiryNotSet")}</span>
        ) : null}
        {formattedIssue && (
          <div>
            <span className="text-muted">{t("adminReviewIssueDate")}: </span>
            <span className="fw-semibold">{formattedIssue}</span>
          </div>
        )}
        {formattedChecked && (
          <div>
            <span className="text-muted">
              {t("adminReviewLastCheckedDate")}:{" "}
            </span>
            <span className="fw-semibold">{formattedChecked}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminReviewPanel({ title, description, children }) {
  return (
    <div
      className="worker-admin-review-panel rounded border p-3 mb-0"
      style={{ backgroundColor: "#f8f9fb", borderColor: "#dee2e6" }}
    >
      <div className="d-flex align-items-start gap-2 mb-3">
        <i
          className="mdi mdi-shield-check-outline text-primary fs-5"
          aria-hidden
        />
        <div>
          <h6 className="mb-1 fw-bolder">{title}</h6>
          {description ? (
            <p className="text-muted small mb-0">{description}</p>
          ) : null}
        </div>
      </div>
      <Row className="gy-3 gx-3">{children}</Row>
    </div>
  );
}

function isYupObjectLevelError(error) {
  return typeof error === "string";
}

// ---------------------------------------------------

const WorkerUpdate = (props) => {
  const { t } = props;
  document.title = t("workerUpdatePageTitle", "Worker Update | JobArk");
  const nationalityOptions = useMemo(() => getNationalityOptions(), []);
  const addressCountryOptions = useMemo(
    () => [{ value: UK_COUNTRY, label: UK_COUNTRY }],
    []
  );
  const googleAutocompleteOptions = useMemo(
    () => ({ componentRestrictions: { country: "gb" } }),
    []
  );
  const googleMapsLibraries = useMemo(() => ["places"], []);
  const { isLoaded: mapsLoaded } = useLoadScript({
    id: "worker-profile-google-maps",
    googleMapsApiKey: GOOGLE_MAP_KEY || "",
    libraries: googleMapsLibraries,
  });
  const addressAutocompleteRef = useRef(null);
  const nextCertEntryIdRef = useRef(1);
  const certFileInputRefsRef = useRef({});

  // APIs
  const userMe = useUserMe();
  const isProfileLoading = userMe.isPending;
  const updateUser = useUpdateWorkerUser();

  // Sectors + Roles
  const { data: sectorsDataRes, isLoading: sectorsLoading } =
    useGetAllSectorsNotPaginated();
  const sectorsData = useMemo(() => {
    const raw = sectorsDataRes?.data || [];
    return Array.isArray(raw) ? raw : [];
  }, [sectorsDataRes]);

  const {
    mutate: loadCerts,
    data: certsDataRes,
    isPending: certsLoading,
  } = useGetAllCertsNotPaginated();
  const certsData = useMemo(() => {
    const raw = certsDataRes?.data?.data || certsDataRes?.data || [];
    const filtered = Array.isArray(raw)
      ? raw.filter((cert) => cert?.isActive)
      : [];
    // Sort so DBS certificates (hasServiceNumber) appear first
    return filtered.sort((a, b) => {
      const aIsDbs = a?.hasServiceNumber ? 1 : 0;
      const bIsDbs = b?.hasServiceNumber ? 1 : 0;
      return bIsDbs - aIsDbs; // DBS certs first (1 - 0 = 1, 0 - 1 = -1)
    });
  }, [certsDataRes]);

  // useEffect(() => {
  //   userMe.mutate();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  // useEffect(() => {
  //   loadSectors();
  // }, [loadSectors]);

  useEffect(() => {
    loadCerts();
  }, [loadCerts]);

  // ---- shape user → initialValues (plus expose existing file URLs/keys) ----
  const me = useMemo(() => {
    const raw = userMe?.data?.data ?? userMe?.data ?? {};
    return mapWorkerApiToFormValues(raw);
  }, [userMe.data]);

  const employmentTaxExistingP45 = useMemo(() => {
    const tax = me?.employmentTax || {};
    return Array.isArray(tax.p45FileUrls)
      ? tax.p45FileUrls.filter(Boolean)
      : [];
  }, [me]);

  const initialValues = useMemo(() => ({ ...me, role: "worker" }), [me]);

  useEffect(() => {
    const entries = me?.certificateEntries || [];
    const maxId = entries.reduce((m, e) => Math.max(m, e?.id || 0), 0);
    nextCertEntryIdRef.current = maxId + 1;
  }, [me]);

  // Collapsible sections state
  const [openPersonal, setOpenPersonal] = useState(true);
  const [openEmergency, setOpenEmergency] = useState(false);
  const [openRightToWork, setOpenRightToWork] = useState(false);
  const [openOnScreeningVideo, setOpenOnScreeningVideo] = useState(false);
  const [openEmploymentTax, setOpenEmploymentTax] = useState(false);
  const [openBankDetails, setOpenBankDetails] = useState(false);
  const [openMoreAboutYou, setOpenMoreAboutYou] = useState(false);
  const [openWorkPreferences, setOpenWorkPreferences] = useState(false);
  const [openCompliance, setOpenCompliance] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: buildWorkerProfileValidationSchema(t),
    onSubmit: async (
      values,
      { setSubmitting, setFieldError, setFieldTouched }
    ) => {
      const certErrors = validateCertificateEntries(values, certsData, t);
      if (certErrors) {
        setFieldError("certificateEntries", certErrors);
        setFieldTouched("certificateEntries", true);
        setSubmitting(false);
        return;
      }
      try {
        const fd = buildWorkerProfileFormData(values, certsData);
        await updateUser.mutateAsync(fd);
        toastr.success(
          t("updatedWorkerProfile") || "Worker updated successfully"
        );
        await userMe.refetch();
      } catch (e) {
        toastr.error(
          e?.response?.data?.message ||
            t("tryAgain") ||
            "Update failed. Please try again."
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const tt = (key, fallback) => t(key, { defaultValue: fallback ?? key });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    isSubmitting,
    submitCount,
  } = formik;

  const nationalitySelectOptions = useMemo(
    () =>
      countryOptionsIncludingValue(
        nationalityOptions,
        values.personalInformation.nationality
      ),
    [nationalityOptions, values.personalInformation.nationality]
  );

  const countrySelectOptions = addressCountryOptions;

  const emergencyRelationshipValue =
    values.emergencyContact.relationshipToWorker;
  const hasCustomEmergencyRelationship =
    !!emergencyRelationshipValue &&
    !EMERGENCY_RELATIONSHIP_OPTIONS.some(
      (o) => o.value === emergencyRelationshipValue
    );

  // Helper to get the first error field path
  const getFirstErrorField = (errors) => {
    const findFirstError = (obj, prefix = "") => {
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const currentPath = prefix ? `${prefix}.${key}` : key;
          const value = obj[key];

          if (value && typeof value === "object" && !Array.isArray(value)) {
            // Nested object, recurse
            const nested = findFirstError(value, currentPath);
            if (nested) return nested;
          } else if (value) {
            // This is an error (string or truthy value)
            return currentPath;
          }
        }
      }
      return null;
    };

    return findFirstError(errors);
  };

  // Scroll to first error field on form submission with errors
  useEffect(() => {
    if (submitCount > 0 && Object.keys(errors).length > 0) {
      const firstErrorPath = getFirstErrorField(errors);
      if (firstErrorPath) {
        // Determine which accordion section this field belongs to and open it if closed
        const firstSection = firstErrorPath.split(".")[0];
        let needsAccordionOpen = false;
        if (firstSection === "personalInformation" && !openPersonal) {
          setOpenPersonal(true);
          needsAccordionOpen = true;
        } else if (firstSection === "emergencyContact" && !openEmergency) {
          setOpenEmergency(true);
          needsAccordionOpen = true;
        } else if (firstSection === "rightToWork" && !openRightToWork) {
          setOpenRightToWork(true);
          needsAccordionOpen = true;
        } else if (firstSection === "employmentTax" && !openEmploymentTax) {
          setOpenEmploymentTax(true);
          needsAccordionOpen = true;
        } else if (firstSection === "bankDetails" && !openBankDetails) {
          setOpenBankDetails(true);
          needsAccordionOpen = true;
        } else if (firstSection === "workPreferences" && !openWorkPreferences) {
          setOpenWorkPreferences(true);
          needsAccordionOpen = true;
        } else if (firstSection === "moreAboutYou" && !openMoreAboutYou) {
          setOpenMoreAboutYou(true);
          needsAccordionOpen = true;
        } else if (firstSection === "certificateEntries" && !openCompliance) {
          setOpenCompliance(true);
          needsAccordionOpen = true;
        }

        // Convert path to a selector (e.g., "personalInformation.fullName" -> "personalInformation-fullName")
        const fieldId = firstErrorPath.replace(/\./g, "-");
        const fieldName = firstErrorPath.split(".").pop();

        // Try to find the input element by name or id
        let element = null;

        // First, try to find by exact name attribute
        const nameSelector = `[name="${firstErrorPath}"]`;
        element = document.querySelector(nameSelector);

        // If not found, try by id
        if (!element) {
          element = document.getElementById(fieldId);
        }

        // If still not found, try to find by the last part of the path (field name)
        if (!element) {
          const fieldNameSelector = `[name="${fieldName}"]`;
          element = document.querySelector(fieldNameSelector);
        }

        // If still not found, look for invalid fields (Formik adds invalid class)
        if (!element) {
          // Find all invalid inputs
          const invalidInputs = document.querySelectorAll(
            "input.is-invalid, select.is-invalid, textarea.is-invalid"
          );
          if (invalidInputs.length > 0) {
            element = invalidInputs[0];
          }
        }

        // For PhoneInput components, look for the container
        if (!element) {
          const phoneInputs = document.querySelectorAll(".react-tel-input");
          for (const phoneInput of phoneInputs) {
            const input = phoneInput.querySelector("input");
            if (input && input.getAttribute("name") === firstErrorPath) {
              element = phoneInput;
              break;
            }
          }
        }

        // For react-select components, look for the control
        if (!element) {
          const selectControls = document.querySelectorAll(
            ".react-select__control"
          );
          for (const control of selectControls) {
            const input = control.querySelector("input");
            if (input && input.getAttribute("name") === firstErrorPath) {
              element = control.closest(".react-select-container") || control;
              break;
            }
          }
        }

        // If element found, scroll to it
        if (element) {
          // Add extra delay if accordion was just opened to allow animation to complete
          const scrollDelay = needsAccordionOpen ? 400 : 100;
          setTimeout(() => {
            // Scroll to the element's parent Card if it exists, otherwise scroll to the element
            const card = element.closest(".card");
            const scrollTarget = card || element;

            scrollTarget.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });

            // Focus the element if it's focusable
            if (element.focus && typeof element.focus === "function") {
              setTimeout(() => element.focus(), 200);
            } else {
              // Try to find a focusable child
              const focusable = element.querySelector(
                "input, select, textarea"
              );
              if (focusable && focusable.focus) {
                setTimeout(() => focusable.focus(), 200);
              }
            }
          }, scrollDelay);
        } else {
          // Fallback: scroll to top of form
          const formElement = document.querySelector("form");
          if (formElement) {
            setTimeout(() => {
              formElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }, 100);
          }
        }
      }
    }
  }, [
    submitCount,
    errors,
    touched,
    openPersonal,
    openEmergency,
    openRightToWork,
    openEmploymentTax,
    openBankDetails,
    openMoreAboutYou,
    openWorkPreferences,
    openCompliance,
  ]);

  // --- helpers ---
  const setNested = useCallback(
    (path, val) => setFieldValue(path, val),
    [setFieldValue]
  );
  const touchNested = useCallback(
    (path) => setFieldTouched(path, true),
    [setFieldTouched]
  );
  const setChecked = (path) => (e) => setNested(path, !!e.target.checked);
  const toggleInArray = (path, val) => {
    const arr = new Set(getAt(values, path) || []);
    if (arr.has(val)) arr.delete(val);
    else arr.add(val);
    setNested(path, Array.from(arr));
  };
  const toggleWhatMattersMost = (opt) => {
    const arr = new Set(
      normalizeWhatMattersMost(values.workPreferences.whatMattersMost)
    );
    if (arr.has(opt)) arr.delete(opt);
    else arr.add(opt);
    setNested("workPreferences.whatMattersMost", Array.from(arr));
    setFieldTouched("workPreferences.whatMattersMost", true);
  };

  const handleFileChange = (path, file, maxMB) => {
    if (file && !isUnder(file, maxMB)) {
      toastr.error(t("fileTooLarge", { n: maxMB }));
      setNested(path, null);
      return;
    }
    if (file && !FILE_VALIDATION.ALLOWED_EXTENSIONS.test(file.name)) {
      toastr.error(t("fileTypeNotAllowed"));
      setNested(path, null);
      return;
    }
    setNested(path, file || null);
    touchNested(path);
  };

  const handleFilesChange = (path, files, inputEl) => {
    const maxCount = FILE_VALIDATION.MAX_FILES_PER_FIELD;
    const maxMB = FILE_VALIDATION.MAX_MB;
    const list = Array.from(files || []);
    if (!list.length) {
      setNested(path, []);
      return;
    }
    if (list.length > maxCount) {
      toastr.error(t("maxFilesAllowed", { n: maxCount }));
      setNested(path, []);
      if (inputEl) inputEl.value = "";
      return;
    }
    for (const f of list) {
      if (!isUnder(f, maxMB)) {
        toastr.error(t("fileTooLarge", { n: maxMB }));
        setNested(path, []);
        if (inputEl) inputEl.value = "";
        return;
      }
      if (!FILE_VALIDATION.ALLOWED_EXTENSIONS.test(f.name)) {
        toastr.error(t("fileTypeNotAllowed"));
        setNested(path, []);
        if (inputEl) inputEl.value = "";
        return;
      }
    }
    setNested(path, list);
    touchNested(path);
  };

  const handleSortCodeChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length > 6) value = value.slice(0, 6);
    if (value.length > 4) {
      value = `${value.slice(0, 2)}-${value.slice(2, 4)}-${value.slice(4)}`;
    } else if (value.length > 2) {
      value = `${value.slice(0, 2)}-${value.slice(2)}`;
    }
    setNested("bankDetails.sortCode", value);
  };

  const reverseGeocodePlace = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&region=gb&key=${GOOGLE_MAP_KEY}`
      );
      const data = await response.json();
      if (data.status === "OK" && data.results?.length > 0) {
        const ukResult = data.results.find((result) =>
          result.address_components?.some(
            (component) =>
              component.types.includes("country") &&
              component.short_name === "GB"
          )
        );
        return ukResult || null;
      }
      return null;
    } catch {
      return null;
    }
  };

  const requestLocation = () => {
    if (!("geolocation" in navigator)) {
      toastr.error(
        t("browserNoGeo") || "Geolocation is not supported by this browser."
      );
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const latStr = String(latitude.toFixed(6));
        const lngStr = String(longitude.toFixed(6));
        setNested("personalInformation.baseLat", latStr);
        setNested("personalInformation.baseLng", lngStr);
        const place = await reverseGeocodePlace(latitude, longitude);
        if (!place) {
          setGeoLoading(false);
          toastr.error(
            t(
              "locationMustBeUk",
              "Your location must be in the United Kingdom."
            )
          );
          return;
        }
        const parsed = parseGoogleAddressComponents(place);
        if (parsed) {
          if (parsed.addressLine1)
            setNested("personalInformation.addressLine1", parsed.addressLine1);
          if (parsed.addressLine2)
            setNested("personalInformation.addressLine2", parsed.addressLine2);
          if (parsed.postcode)
            setNested("personalInformation.postcode", parsed.postcode);
          if (parsed.city) setNested("personalInformation.city", parsed.city);
          if (parsed.county)
            setNested("personalInformation.county", parsed.county);
          setNested("personalInformation.country", UK_COUNTRY);
          if (parsed.formatted)
            setNested("personalInformation.addressSearch", parsed.formatted);
        }
        setGeoLoading(false);
        toastr.success(t("locationCaptured") || "Location captured.");
      },
      (err) => {
        setGeoLoading(false);
        toastr.error(
          err?.message ||
            t("unableGetLocation") ||
            "Unable to get your location."
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const setConvictionsChoice = async (choice) => {
    const nextMoreAboutYou = {
      ...values.moreAboutYou,
      ...(choice === "none"
        ? {
            hasNoConvictions: true,
            hasConvictionsOrPending: false,
            criminalConvictionsDetails: "",
          }
        : {
            hasConvictionsOrPending: true,
            hasNoConvictions: false,
          }),
    };
    await setFieldValue("moreAboutYou", nextMoreAboutYou, false);
    setFieldTouched(
      choice === "none"
        ? "moreAboutYou.hasNoConvictions"
        : "moreAboutYou.hasConvictionsOrPending",
      true,
      false
    );
    if (isYupObjectLevelError(errors.moreAboutYou)) {
      setFieldError("moreAboutYou", undefined);
    }
  };

  const moreAboutYouFieldShowError = (field) =>
    (!!touched.moreAboutYou?.[field] || submitCount > 0) &&
    !!errors.moreAboutYou?.[field];

  const convictionsChoiceError =
    typeof errors.moreAboutYou === "string" ? errors.moreAboutYou : null;
  const convictionsRadiosShowError =
    (!!touched.moreAboutYou?.hasNoConvictions ||
      !!touched.moreAboutYou?.hasConvictionsOrPending ||
      submitCount > 0) &&
    !!convictionsChoiceError;

  const addCertificateEntry = () => {
    const list = [...(values.certificateEntries || [])];
    list.push({
      id: nextCertEntryIdRef.current++,
      certSlug: "",
      file: null,
      serviceNumber: "",
      existingFiles: [],
    });
    setFieldValue("certificateEntries", list);
  };

  const removeCertificateEntry = (index) => {
    const list = [...(values.certificateEntries || [])];
    list.splice(index, 1);
    setFieldValue(
      "certificateEntries",
      list.length
        ? list
        : [
            {
              id: nextCertEntryIdRef.current++,
              certSlug: "",
              file: null,
              serviceNumber: "",
              existingFiles: [],
            },
          ]
    );
  };

  const handleCertificateFile = (index, file, inputEl) => {
    if (file && !isUnder(file, FILE_VALIDATION.MAX_MB)) {
      toastr.error(t("fileTooLarge", { n: FILE_VALIDATION.MAX_MB }));
      if (inputEl) inputEl.value = "";
      return;
    }
    if (file && !FILE_VALIDATION.ALLOWED_EXTENSIONS.test(file.name)) {
      toastr.error(t("fileTypeNotAllowed"));
      if (inputEl) inputEl.value = "";
      return;
    }
    const list = [...(values.certificateEntries || [])];
    if (!list[index]) return;
    list[index] = { ...list[index], file: file || null };
    setFieldValue("certificateEntries", list);
  };

  const handleDbsCertificateServiceNumberChange = (index, _entryId, value) => {
    const list = [...(values.certificateEntries || [])];
    if (!list[index]) return;
    list[index] = { ...list[index], serviceNumber: value };
    setFieldValue("certificateEntries", list);
  };

  const clearFileInput = useCallback((id) => {
    if (typeof document === "undefined") return;
    const el = document.getElementById(id);
    if (el) el.value = "";
  }, []);

  const clearCertificateEntryFile = (index, entryId) => {
    const list = [...(values.certificateEntries || [])];
    if (!list[index]) return;
    list[index] = { ...list[index], file: null };
    setFieldValue("certificateEntries", list);
    if (entryId != null) {
      const input = certFileInputRefsRef.current[entryId];
      if (input) input.value = "";
    }
  };

  const onLoadAddressAutocomplete = (autocomplete) => {
    addressAutocompleteRef.current = autocomplete;
  };

  const onAddressPlaceChanged = () => {
    const place = addressAutocompleteRef.current?.getPlace?.();
    if (!place) return;
    const parsed = parseGoogleAddressComponents(place);
    if (!parsed) return;
    if (parsed.addressLine1)
      setNested("personalInformation.addressLine1", parsed.addressLine1);
    if (parsed.addressLine2)
      setNested("personalInformation.addressLine2", parsed.addressLine2);
    if (parsed.postcode)
      setNested("personalInformation.postcode", parsed.postcode);
    if (parsed.city) setNested("personalInformation.city", parsed.city);
    if (parsed.county) setNested("personalInformation.county", parsed.county);
    setNested("personalInformation.country", UK_COUNTRY);
    if (parsed.lat) setNested("personalInformation.baseLat", parsed.lat);
    if (parsed.lng) setNested("personalInformation.baseLng", parsed.lng);
    touchNested("personalInformation.addressLine1");
    touchNested("personalInformation.postcode");
    touchNested("personalInformation.city");
    touchNested("personalInformation.country");
  };

  useEffect(() => {
    const status = values.rightToWork?.rightToWorkStatus;
    if (status === RIGHT_TO_WORK_STATUS_BRITISH_IRISH) {
      if (values.rightToWork?.shareCode)
        setFieldValue("rightToWork.shareCode", "");
      if (values.rightToWork?.visaExpiryDate)
        setFieldValue("rightToWork.visaExpiryDate", "");
    }
  }, [
    values.rightToWork?.rightToWorkStatus,
    values.rightToWork?.shareCode,
    values.rightToWork?.visaExpiryDate,
    setFieldValue,
  ]);

  useEffect(() => {
    if (values.employmentTax?.hasP45 === "yes") {
      if (values.employmentTax?.starterChecklistChoice) {
        setNested("employmentTax.starterChecklistChoice", "");
      }
      if (values.employmentTax?.hasPostgraduateLoan) {
        setNested("employmentTax.hasPostgraduateLoan", "");
      }
      if (values.employmentTax?.planType) {
        setNested("employmentTax.planType", "");
      }
    } else if (values.employmentTax?.hasP45 === "no") {
      if (values.employmentTax?.p45Upload) {
        setNested("employmentTax.p45Upload", null);
        clearFileInput("p45UploadInput");
      }
    }
  }, [
    values.employmentTax?.hasP45,
    values.employmentTax?.p45Upload,
    values.employmentTax?.starterChecklistChoice,
    values.employmentTax?.hasPostgraduateLoan,
    values.employmentTax?.planType,
    setNested,
    clearFileInput,
  ]);

  // ---- sectors helpers ----
  const sectorOptions = useMemo(
    () =>
      sectorsData.map((s) => ({
        value: s.slug,
        label: s.title,
        roles: s.roles || [],
      })),
    [sectorsData]
  );

  const getSectorOptionsForRow = (rowIndex) => {
    const selectedElsewhere = new Set(
      (values.workPreferences.sectors || [])
        .map((s, i) => (i === rowIndex ? null : s.sectorSlug))
        .filter(Boolean)
    );
    return sectorOptions.map((opt) => ({
      ...opt,
      disabled: selectedElsewhere.has(opt.value),
    }));
  };

  const addSector = () => {
    const list = values.workPreferences.sectors || [];
    setNested("workPreferences.sectors", [
      ...list,
      { sectorSlug: "", roles: [] },
    ]);
  };
  const removeSector = (idx) => {
    if (idx === 0) return;
    const list = [...(values.workPreferences.sectors || [])];
    list.splice(idx, 1);
    setNested(
      "workPreferences.sectors",
      list.length ? list : [{ sectorSlug: "", roles: [] }]
    );
  };
  const setSectorSlug = (idx, slug) => {
    const selectedElsewhere = new Set(
      (values.workPreferences.sectors || [])
        .map((s, i) => (i === idx ? null : s.sectorSlug))
        .filter(Boolean)
    );
    if (slug && selectedElsewhere.has(slug)) {
      toastr.warning(
        t("sectorAlreadySelected") ||
          "This sector is already selected in another row."
      );
      return;
    }
    const list = [...(values.workPreferences.sectors || [])];
    list[idx] = { sectorSlug: slug, roles: [] };
    setNested("workPreferences.sectors", list);
    touchNested("workPreferences.sectors");
  };
  const getRoleOptions = (sectorSlug) => {
    const sec = sectorsData.find((s) => s.slug === sectorSlug);
    return (sec?.roles || []).map((r) => ({ value: r.slug, label: r.title }));
  };
  const setRolesForSector = (idx, roleSlugs) => {
    const list = [...(values.workPreferences.sectors || [])];
    list[idx] = { ...list[idx], roles: roleSlugs };
    setNested("workPreferences.sectors", list);
    touchNested("workPreferences.sectors");
  };

  const invalid = (path) => !!getAt(touched, path) && !!getAt(errors, path);

  const renderStoredFiles = (urls, max = 2) => {
    const list = (Array.isArray(urls) ? urls : []).filter(Boolean);
    if (!list.length) return null;
    const shown = list.slice(0, max);
    return (
      <div className="d-flex flex-wrap gap-2 mb-2">
        {shown.map((u, i) => (
          <FileMediaPreview
            key={`${u}-${i}`}
            source={u}
            alt={t("viewCurrentFile")}
            linkLabel={`${t("viewCurrentFile")}${
              shown.length > 1 ? ` ${i + 1}` : ""
            }`}
          />
        ))}
        {list.length > max && (
          <small className="text-muted align-self-center">
            +{list.length - max} {t("more")}
          </small>
        )}
      </div>
    );
  };

  const renderSelectedFiles = (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return null;
    return (
      <div className="d-flex flex-wrap gap-2 mb-2">
        {list.map((file, i) =>
          isImageMedia(file) ? (
            <FileMediaPreview
              key={`sel-${i}-${file.name}`}
              source={file}
              alt={t("selectedFile")}
            />
          ) : (
            <span
              key={`sel-${i}`}
              className="text-muted small align-self-center"
            >
              {t("selectedFile")}
            </span>
          )
        )}
      </div>
    );
  };

  // Worker nested data (on-screening video, etc.)
  const workerData = useMemo(() => {
    const raw = userMe?.data?.data ?? userMe?.data ?? {};
    return raw.worker ?? {};
  }, [userMe?.data]);

  const onScreeningVideoSrc = useMemo(() => {
    const path = workerData?.rightToWork?.onscreeningVideo;
    return path ? mediaUrl(path) : "";
  }, [workerData?.rightToWork?.onscreeningVideo]);

  return (
    <div className="page-content">
      <Container fluid className="px-4">
        <Row>
          <Col xs={12}>
            {userMe.isError && (
              <Alert color="danger" className="mb-3">
                {t("failedLoadUser") || "Failed to load user."}
              </Alert>
            )}

            {isProfileLoading ? (
              <WorkerProfileSkeleton />
            ) : (
              <>
                <RSForm
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                    return false;
                  }}
                >
                  {/* ===== Personal Information ===== */}
                  <Card className="mb-3">
                    <CardHeader
                      onClick={() => setOpenPersonal((v) => !v)}
                      className="d-flex justify-content-between align-items-center"
                      style={{ cursor: "pointer", backgroundColor: "#eceeee" }}
                    >
                      <h6 className="mb-0 fw-bolder">
                        {t("personalInformation")}
                      </h6>
                      <i
                        className={`mdi mdi-chevron-${
                          openPersonal ? "up" : "down"
                        } bg-white text-secondary border border-secondary rounded-circle`}
                        style={{
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                        }}
                      ></i>
                    </CardHeader>
                    <Collapse isOpen={openPersonal}>
                      <CardBody style={{ backgroundColor: "#eceeee" }}>
                        <Row className="gy-3 gx-4">
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">
                              {t("firstName")}
                            </Label>
                            <Input
                              name="personalInformation.firstName"
                              value={values.personalInformation.firstName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={invalid("personalInformation.firstName")}
                              placeholder={t("placeholderFirstName")}
                            />
                            <FormFeedback>
                              {errors.personalInformation?.firstName}
                            </FormFeedback>
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">{t("lastName")}</Label>
                            <Input
                              name="personalInformation.lastName"
                              value={values.personalInformation.lastName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={invalid("personalInformation.lastName")}
                              placeholder={t("placeholderLastName")}
                            />
                            <FormFeedback>
                              {errors.personalInformation?.lastName}
                            </FormFeedback>
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">{t("dob")}</Label>
                            <Input
                              type="date"
                              name="personalInformation.dob"
                              max={new Date().toISOString().split("T")[0]}
                              value={values.personalInformation.dob}
                              readOnly
                              className="bg-light"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={invalid("personalInformation.dob")}
                            />
                            <FormFeedback>
                              {errors.personalInformation?.dob}
                            </FormFeedback>
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">{t("gender")}</Label>
                            <Input
                              type="select"
                              name="personalInformation.gender"
                              value={values.personalInformation.gender}
                              disabled
                              className="bg-light"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={invalid("personalInformation.gender")}
                            >
                              <option value="">{t("select")}</option>
                              <option value={GENDER_OPTIONS.MALE}>
                                {t("male")}
                              </option>
                              <option value={GENDER_OPTIONS.FEMALE}>
                                {t("female")}
                              </option>
                              <option value={GENDER_OPTIONS.OTHER}>
                                {t("other")}
                              </option>
                            </Input>
                            <FormFeedback>
                              {errors.personalInformation?.gender}
                            </FormFeedback>
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">
                              {t("phoneNumber")}
                            </Label>
                            <FormPhoneInput
                              name="personalInformation.phoneNumber"
                              value={values.personalInformation.phoneNumber}
                              touched={touched.personalInformation?.phoneNumber}
                              error={errors.personalInformation?.phoneNumber}
                              onBlur={() =>
                                touchNested("personalInformation.phoneNumber")
                              }
                              onChange={(v) =>
                                setNested("personalInformation.phoneNumber", v)
                              }
                            />
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">{t("email")}</Label>
                            <Input
                              type="email"
                              name="personalInformation.email"
                              value={values.personalInformation.email}
                              readOnly
                              className="bg-light"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={invalid("personalInformation.email")}
                              placeholder={t("placeholderEmail")}
                            />
                            <FormFeedback>
                              {errors.personalInformation?.email}
                            </FormFeedback>
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">
                              {t("nationality")}
                            </Label>
                            <Select
                              options={nationalitySelectOptions}
                              value={resolveSelectValue(
                                nationalitySelectOptions,
                                values.personalInformation.nationality
                              )}
                              onChange={(selectedOption) => {
                                setNested(
                                  "personalInformation.nationality",
                                  selectedOption ? selectedOption.value : ""
                                );
                              }}
                              onBlur={() =>
                                touchNested("personalInformation.nationality")
                              }
                              placeholder={t("placeholderNationality")}
                              isSearchable
                              isClearable
                              classNamePrefix="rs"
                              menuPortalTarget={
                                typeof document !== "undefined"
                                  ? document.body
                                  : null
                              }
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                                control: (base, state) => ({
                                  ...base,
                                  minHeight: 38,
                                  borderColor:
                                    touched.personalInformation?.nationality &&
                                    errors.personalInformation?.nationality
                                      ? "#dc3545"
                                      : state.isFocused
                                      ? "#80bdff"
                                      : base.borderColor,
                                }),
                              }}
                            />
                            {touched.personalInformation?.nationality &&
                              errors.personalInformation?.nationality && (
                                <div className="invalid-feedback d-block">
                                  {errors.personalInformation?.nationality}
                                </div>
                              )}
                          </Col>
                          <Col md={12}>
                            <Label className="fw-bolder">
                              {t("languagesSpoken")}
                            </Label>
                            <CreatableSelect
                              isMulti
                              isSearchable
                              isClearable
                              options={getLanguageOptions()}
                              value={(
                                values.personalInformation.languagesSpoken || []
                              ).map((lang) => ({ value: lang, label: lang }))}
                              onChange={(selectedOptions) => {
                                const next = selectedOptions
                                  ? selectedOptions.map((o) =>
                                      String(o.value).trim()
                                    )
                                  : [];
                                setNested(
                                  "personalInformation.languagesSpoken",
                                  next.filter(Boolean)
                                );
                              }}
                              onBlur={() =>
                                touchNested(
                                  "personalInformation.languagesSpoken"
                                )
                              }
                              formatCreateLabel={(inputValue) =>
                                `${t("addAnother")}: "${inputValue}"`
                              }
                              classNamePrefix="rs"
                              menuPortalTarget={
                                typeof document !== "undefined"
                                  ? document.body
                                  : null
                              }
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                                control: (base) => ({
                                  ...base,
                                  minHeight: 44,
                                  borderColor:
                                    touched.personalInformation
                                      ?.languagesSpoken &&
                                    errors.personalInformation?.languagesSpoken
                                      ? "#dc3545"
                                      : base.borderColor,
                                }),
                              }}
                              placeholder={t("selectLanguages")}
                            />
                            {touched.personalInformation?.languagesSpoken &&
                              errors.personalInformation?.languagesSpoken && (
                                <div className="invalid-feedback d-block">
                                  {errors.personalInformation?.languagesSpoken}
                                </div>
                              )}
                          </Col>
                          <Col md={12}>
                            <Label className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                              <span>{t("addressSearchLabel")}</span>
                              <Button
                                type="button"
                                color="secondary"
                                size="sm"
                                onClick={requestLocation}
                                disabled={geoLoading}
                              >
                                {geoLoading && (
                                  <span className="spinner-border spinner-border-sm me-2" />
                                )}
                                {t("useMyLocation")}
                              </Button>
                            </Label>
                            {mapsLoaded ? (
                              <Autocomplete
                                onLoad={onLoadAddressAutocomplete}
                                onPlaceChanged={onAddressPlaceChanged}
                                options={googleAutocompleteOptions}
                              >
                                <Input
                                  name="personalInformation.addressSearch"
                                  value={
                                    values.personalInformation.addressSearch ||
                                    ""
                                  }
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  placeholder={t("addressSearchPlaceholder")}
                                />
                              </Autocomplete>
                            ) : (
                              <Input
                                disabled
                                placeholder={t("loadingMapsPlaceholder")}
                              />
                            )}
                            <small className="text-muted d-block mt-1">
                              {t("addressSearchHelp")}
                            </small>
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">
                              {t("addressLine1")}
                            </Label>
                            <Input
                              name="personalInformation.addressLine1"
                              value={
                                values.personalInformation.addressLine1 || ""
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={invalid(
                                "personalInformation.addressLine1"
                              )}
                            />
                            <FormFeedback>
                              {errors.personalInformation?.addressLine1}
                            </FormFeedback>
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">
                              {t("addressLine2")}
                            </Label>
                            <Input
                              name="personalInformation.addressLine2"
                              value={
                                values.personalInformation.addressLine2 || ""
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">{t("postcode")}</Label>
                            <Input
                              name="personalInformation.postcode"
                              value={values.personalInformation.postcode || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={invalid("personalInformation.postcode")}
                            />
                            <FormFeedback>
                              {errors.personalInformation?.postcode}
                            </FormFeedback>
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">{t("city")}</Label>
                            <Input
                              name="personalInformation.city"
                              value={values.personalInformation.city || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={invalid("personalInformation.city")}
                            />
                            <FormFeedback>
                              {errors.personalInformation?.city}
                            </FormFeedback>
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">{t("county")}</Label>
                            <Input
                              name="personalInformation.county"
                              value={values.personalInformation.county || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">{t("country")}</Label>
                            <Select
                              options={countrySelectOptions}
                              value={countrySelectOptions.find(
                                (option) =>
                                  option.value ===
                                  values.personalInformation.country
                              )}
                              onChange={() =>
                                setNested(
                                  "personalInformation.country",
                                  UK_COUNTRY
                                )
                              }
                              onBlur={() =>
                                touchNested("personalInformation.country")
                              }
                              placeholder={t("placeholderCountry")}
                              isSearchable={false}
                              isClearable={false}
                              isDisabled
                              classNamePrefix="rs"
                              menuPortalTarget={
                                typeof document !== "undefined"
                                  ? document.body
                                  : null
                              }
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                                control: (base, state) => ({
                                  ...base,
                                  minHeight: 38,
                                  borderColor:
                                    touched.personalInformation?.country &&
                                    errors.personalInformation?.country
                                      ? "#dc3545"
                                      : state.isFocused
                                      ? "#80bdff"
                                      : base.borderColor,
                                }),
                              }}
                            />
                            {touched.personalInformation?.country &&
                              errors.personalInformation?.country && (
                                <div className="invalid-feedback d-block">
                                  {errors.personalInformation?.country}
                                </div>
                              )}
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">
                              {t("niNumber")}{" "}
                              <span className="text-muted small">
                                ({t("optional")})
                              </span>
                            </Label>
                            <Input
                              name="personalInformation.nationalInsuranceNumber"
                              value={
                                values.personalInformation
                                  .nationalInsuranceNumber
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={invalid(
                                "personalInformation.nationalInsuranceNumber"
                              )}
                              placeholder={t("placeholderNiNumber")}
                            />
                            <FormFeedback>
                              {
                                errors.personalInformation
                                  ?.nationalInsuranceNumber
                              }
                            </FormFeedback>
                          </Col>
                          <Col md={12} className="d-none">
                            <Input
                              type="hidden"
                              name="personalInformation.baseLat"
                              value={values.personalInformation.baseLat || ""}
                            />
                            <Input
                              type="hidden"
                              name="personalInformation.baseLng"
                              value={values.personalInformation.baseLng || ""}
                            />
                          </Col>
                          {(touched.personalInformation?.baseLat ||
                            touched.personalInformation?.baseLng) &&
                            (errors.personalInformation?.baseLat ||
                              errors.personalInformation?.baseLng) && (
                              <Col md={12}>
                                <div className="invalid-feedback d-block">
                                  {errors.personalInformation?.baseLat ||
                                    errors.personalInformation?.baseLng ||
                                    t("pleaseCaptureLocation")}
                                </div>
                              </Col>
                            )}
                          <Col md={12}>
                            <Label className="fw-bolder">
                              {t("uploadSelfie")}{" "}
                              {t("jobarkIdParen", "(JobArk ID)")}
                            </Label>
                            {values.personalInformation.jobarkIdSelfie ? (
                              <FileMediaPreview
                                source={
                                  values.personalInformation.jobarkIdSelfie
                                }
                                alt={t("uploadSelfie") || "Selfie"}
                                maxHeight={200}
                              />
                            ) : values.personalInformation.uploadSelfieUrl ? (
                              <FileMediaPreview
                                source={
                                  values.personalInformation.uploadSelfieUrl
                                }
                                alt={t("uploadSelfie") || "Selfie"}
                                maxHeight={200}
                                linkLabel={t("viewCurrentFile")}
                              />
                            ) : (
                              <small className="text-muted d-block">
                                {t("noSelfieUploaded") ||
                                  "No selfie uploaded yet."}
                              </small>
                            )}
                          </Col>
                        </Row>
                      </CardBody>
                    </Collapse>
                  </Card>

                  {/* ===== Emergency Contact ===== */}
                  <Card className="mb-3">
                    <CardHeader
                      onClick={() => setOpenEmergency((v) => !v)}
                      className="d-flex justify-content-between align-items-center"
                      style={{ cursor: "pointer", backgroundColor: "#eceeee" }}
                    >
                      <h6 className="mb-0 fw-bolder">
                        {t("emergencyContact")}
                      </h6>
                      <i
                        className={`mdi mdi-chevron-${
                          openEmergency ? "up" : "down"
                        } bg-white text-secondary border border-secondary rounded-circle`}
                        style={{
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                        }}
                      ></i>
                    </CardHeader>
                    <Collapse isOpen={openEmergency}>
                      <CardBody style={{ backgroundColor: "#eceeee" }}>
                        <Row className="gy-3 gx-4">
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">
                              {t("firstName")}
                            </Label>
                            <Input
                              name="emergencyContact.firstName"
                              value={values.emergencyContact.firstName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={invalid("emergencyContact.firstName")}
                              placeholder={t("placeholderFirstName")}
                            />
                            <FormFeedback>
                              {errors.emergencyContact?.firstName}
                            </FormFeedback>
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">{t("lastName")}</Label>
                            <Input
                              name="emergencyContact.lastName"
                              value={values.emergencyContact.lastName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={invalid("emergencyContact.lastName")}
                              placeholder={t("placeholderLastName")}
                            />
                            <FormFeedback>
                              {errors.emergencyContact?.lastName}
                            </FormFeedback>
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">
                              {t("relationship")}
                            </Label>
                            <Input
                              type="select"
                              name="emergencyContact.relationshipToWorker"
                              value={
                                values.emergencyContact.relationshipToWorker
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={invalid(
                                "emergencyContact.relationshipToWorker"
                              )}
                            >
                              <option value="">{t("select")}</option>
                              {EMERGENCY_RELATIONSHIP_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {t(opt.labelKey)}
                                </option>
                              ))}
                              {hasCustomEmergencyRelationship && (
                                <option value={emergencyRelationshipValue}>
                                  {emergencyRelationshipValue}
                                </option>
                              )}
                            </Input>
                            <FormFeedback>
                              {errors.emergencyContact?.relationshipToWorker}
                            </FormFeedback>
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">
                              {t("phoneNumber")}
                            </Label>
                            <FormPhoneInput
                              name="emergencyContact.phoneNumber"
                              value={values.emergencyContact.phoneNumber}
                              touched={touched.emergencyContact?.phoneNumber}
                              error={errors.emergencyContact?.phoneNumber}
                              onBlur={() =>
                                touchNested("emergencyContact.phoneNumber")
                              }
                              onChange={(v) =>
                                setNested("emergencyContact.phoneNumber", v)
                              }
                            />
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">
                              {t("altPhone")}{" "}
                              <span className="text-muted small">
                                ({t("optional")})
                              </span>
                            </Label>
                            <FormPhoneInput
                              name="emergencyContact.alternativePhoneNumber"
                              value={
                                values.emergencyContact.alternativePhoneNumber
                              }
                              touched={
                                touched.emergencyContact?.alternativePhoneNumber
                              }
                              error={
                                errors.emergencyContact?.alternativePhoneNumber
                              }
                              onBlur={() =>
                                touchNested(
                                  "emergencyContact.alternativePhoneNumber"
                                )
                              }
                              onChange={(v) =>
                                setNested(
                                  "emergencyContact.alternativePhoneNumber",
                                  v
                                )
                              }
                            />
                          </Col>
                        </Row>
                      </CardBody>
                    </Collapse>
                  </Card>

                  {/* ===== Right to Work ===== */}
                  <Card className="mb-3">
                    <CardHeader
                      onClick={() => setOpenRightToWork((v) => !v)}
                      className="d-flex justify-content-between align-items-center"
                      style={{ cursor: "pointer", backgroundColor: "#eceeee" }}
                    >
                      <h6 className="mb-0 fw-bolder">{t("rightToWork")}</h6>
                      <i
                        className={`mdi mdi-chevron-${
                          openRightToWork ? "up" : "down"
                        } bg-white text-secondary border border-secondary rounded-circle`}
                        style={{
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                        }}
                      ></i>
                    </CardHeader>
                    <Collapse isOpen={openRightToWork}>
                      <CardBody style={{ backgroundColor: "#eceeee" }}>
                        <Row className="gy-3 gx-4">
                          <Col md={12} lg={6}>
                            <Label className="fw-bolder">
                              {t("rightToWorkStatus")}
                            </Label>
                            <Input
                              type="select"
                              name="rightToWork.rightToWorkStatus"
                              value={values.rightToWork.rightToWorkStatus}
                              onChange={(e) => {
                                const next = e.target.value;
                                const prev =
                                  values.rightToWork.rightToWorkStatus;
                                setNested(
                                  "rightToWork.rightToWorkStatus",
                                  next
                                );
                                if (
                                  (prev ===
                                    RIGHT_TO_WORK_STATUS_BRITISH_IRISH) !==
                                  (next === RIGHT_TO_WORK_STATUS_BRITISH_IRISH)
                                ) {
                                  setNested("rightToWork.shareCode", "");
                                  setNested("rightToWork.visaExpiryDate", "");
                                }
                              }}
                              onBlur={handleBlur}
                              invalid={invalid("rightToWork.rightToWorkStatus")}
                            >
                              <option value="">{t("select")}</option>
                              {RIGHT_TO_WORK_STATUS_OPTIONS_ALL.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </Input>
                            <FormFeedback>
                              {errors.rightToWork?.rightToWorkStatus}
                            </FormFeedback>
                          </Col>

                          {!!values.rightToWork.rightToWorkStatus && (
                            <>
                              <Col md={6} lg={3}>
                                <Label className="fw-bolder">
                                  {t("photoIdUpload")}
                                </Label>
                                <small className="text-muted d-block mb-1">
                                  {t("photoIdUploadHint")}
                                </small>
                                {(values.rightToWork.uploadId || []).length > 0
                                  ? renderSelectedFiles(
                                      values.rightToWork.uploadId
                                    )
                                  : renderStoredFiles(
                                      values.rightToWork.uploadIdUrls
                                    )}
                                <Input
                                  type="file"
                                  accept=".pdf,image/*"
                                  multiple
                                  onChange={(e) =>
                                    handleFilesChange(
                                      "rightToWork.uploadId",
                                      e.currentTarget.files,
                                      e.currentTarget
                                    )
                                  }
                                  onBlur={() =>
                                    touchNested("rightToWork.uploadId")
                                  }
                                  invalid={invalid("rightToWork.uploadId")}
                                />
                                {touched.rightToWork?.uploadId &&
                                  errors.rightToWork?.uploadId && (
                                    <div className="invalid-feedback d-block">
                                      {typeof errors.rightToWork?.uploadId ===
                                      "string"
                                        ? errors.rightToWork.uploadId
                                        : t("invalidFiles")}
                                    </div>
                                  )}
                              </Col>
                              <Col md={6} lg={3}>
                                <Label className="fw-bolder">
                                  {t("proofOfAddress")}
                                </Label>
                                {(values.rightToWork.proofOfAddress || [])
                                  .length > 0
                                  ? renderSelectedFiles(
                                      values.rightToWork.proofOfAddress
                                    )
                                  : renderStoredFiles(
                                      values.rightToWork.proofOfAddressUrls
                                    )}
                                <Input
                                  type="file"
                                  accept=".pdf,image/*"
                                  multiple
                                  onChange={(e) =>
                                    handleFilesChange(
                                      "rightToWork.proofOfAddress",
                                      e.currentTarget.files,
                                      e.currentTarget
                                    )
                                  }
                                  onBlur={() =>
                                    touchNested("rightToWork.proofOfAddress")
                                  }
                                  invalid={invalid(
                                    "rightToWork.proofOfAddress"
                                  )}
                                />
                                {touched.rightToWork?.proofOfAddress &&
                                  errors.rightToWork?.proofOfAddress && (
                                    <div className="invalid-feedback d-block">
                                      {typeof errors.rightToWork
                                        ?.proofOfAddress === "string"
                                        ? errors.rightToWork.proofOfAddress
                                        : t("invalidFiles")}
                                    </div>
                                  )}
                              </Col>
                              {values.rightToWork.rightToWorkStatus !==
                                RIGHT_TO_WORK_STATUS_BRITISH_IRISH && (
                                <>
                                  <Col md={6} lg={3}>
                                    <Label className="fw-bolder">
                                      {t("homeOfficeShareCode")}
                                    </Label>
                                    <Input
                                      name="rightToWork.shareCode"
                                      value={values.rightToWork.shareCode}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      invalid={invalid("rightToWork.shareCode")}
                                      placeholder={t("placeholderShareCode")}
                                    />
                                    <FormFeedback>
                                      {errors.rightToWork?.shareCode}
                                    </FormFeedback>
                                  </Col>
                                  <Col md={6} lg={3}>
                                    <Label className="fw-bolder">
                                      {t("visaExpiryDate")}
                                    </Label>
                                    <Input
                                      type="date"
                                      name="rightToWork.visaExpiryDate"
                                      min={
                                        new Date().toISOString().split("T")[0]
                                      }
                                      value={values.rightToWork.visaExpiryDate}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      invalid={invalid(
                                        "rightToWork.visaExpiryDate"
                                      )}
                                    />
                                    <FormFeedback>
                                      {errors.rightToWork?.visaExpiryDate}
                                    </FormFeedback>
                                  </Col>
                                </>
                              )}
                            </>
                          )}
                        </Row>
                      </CardBody>
                    </Collapse>
                  </Card>

                  {/* ===== Meet the Worker ===== */}
                  <Card className="mb-3">
                    <CardHeader
                      onClick={() => setOpenOnScreeningVideo((v) => !v)}
                      className="d-flex justify-content-between align-items-center"
                      style={{ cursor: "pointer", backgroundColor: "#eceeee" }}
                    >
                      <h6 className="mb-0 fw-bolder">
                        {t("onScreeningVideoTitle")}
                      </h6>
                      <i
                        className={`mdi mdi-chevron-${
                          openOnScreeningVideo ? "up" : "down"
                        } bg-white text-secondary border border-secondary rounded-circle`}
                        style={{
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                        }}
                      ></i>
                    </CardHeader>
                    <Collapse isOpen={openOnScreeningVideo}>
                      <CardBody style={{ backgroundColor: "#eceeee" }}>
                        <p className="text-muted small mb-3">
                          {t("onScreeningVideoIntroProfile")}
                        </p>
                        <div className="on-screening-inspiration mb-3">
                          <div
                            className="on-screening-inspiration__icon"
                            aria-hidden="true"
                          >
                            <i className="bx bx-bulb" />
                          </div>
                          <div className="on-screening-inspiration__content">
                            <p className="on-screening-inspiration__lead">
                              {t(
                                "onScreeningQuestionsTitle",
                                "Not sure what to say?"
                              )}
                            </p>
                            <p className="on-screening-inspiration__text">
                              {t(
                                "onScreeningQuestion",
                                "Imagine you're introducing yourself to a client. Why should they choose you?"
                              )}
                            </p>
                          </div>
                        </div>
                        <Label className="fw-bolder d-block mb-2">
                          {t("onScreeningVideoPreviewLabel")}
                        </Label>
                        <div
                          className="rounded border overflow-hidden bg-dark"
                          style={{
                            aspectRatio: "16 / 10",
                            minHeight: 200,
                            maxWidth: 640,
                          }}
                        >
                          {onScreeningVideoSrc ? (
                            <video
                              src={onScreeningVideoSrc}
                              controls
                              playsInline
                              preload="metadata"
                              className="w-100 h-100"
                              style={{
                                objectFit: "contain",
                                maxHeight: 360,
                              }}
                            />
                          ) : (
                            <div className="d-flex align-items-center justify-content-center text-white-50 small p-4 h-100">
                              {t("onScreeningNoVideoYet")}
                            </div>
                          )}
                        </div>
                      </CardBody>
                    </Collapse>
                  </Card>

                  {/* ===== Employment & Tax ===== */}
                  <Card className="mb-3">
                    <CardHeader
                      onClick={() => setOpenEmploymentTax((v) => !v)}
                      className="d-flex justify-content-between align-items-center"
                      style={{ cursor: "pointer", backgroundColor: "#eceeee" }}
                    >
                      <h6 className="mb-0 fw-bolder">{t("employmentTax")}</h6>
                      <i
                        className={`mdi mdi-chevron-${
                          openEmploymentTax ? "up" : "down"
                        } bg-white text-secondary border border-secondary rounded-circle`}
                        style={{
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                        }}
                      ></i>
                    </CardHeader>
                    <Collapse isOpen={openEmploymentTax}>
                      <CardBody style={{ backgroundColor: "#eceeee" }}>
                        <Row className="gy-3 gx-4">
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">
                              {t("hasP45")}{" "}
                              <span className="text-danger">*</span>
                            </Label>
                            <Input
                              type="select"
                              name="employmentTax.hasP45"
                              value={values.employmentTax.hasP45}
                              onChange={(e) => {
                                const nextValue = e.target.value;
                                setNested("employmentTax.hasP45", nextValue);
                                if (nextValue === "yes") {
                                  setNested(
                                    "employmentTax.starterChecklistChoice",
                                    ""
                                  );
                                  setNested(
                                    "employmentTax.hasPostgraduateLoan",
                                    ""
                                  );
                                  setNested("employmentTax.planType", "");
                                }
                                if (nextValue === "no") {
                                  setNested("employmentTax.p45Upload", null);
                                  clearFileInput("p45UploadInput");
                                }
                              }}
                              onBlur={handleBlur}
                              invalid={
                                touched.employmentTax?.hasP45 &&
                                !!errors.employmentTax?.hasP45
                              }
                            >
                              <option value="">{t("select") || "Select"}</option>
                              <option value="yes">{t("yes") || "Yes"}</option>
                              <option value="no">{t("no") || "No"}</option>
                            </Input>
                            <FormFeedback>
                              {errors.employmentTax?.hasP45}
                            </FormFeedback>
                          </Col>

                          {values.employmentTax.hasP45 === "yes" && (
                            <>
                              <Col md={6} lg={3}>
                                <Label className="fw-bolder">
                                  {t("p45Upload") || "P45 upload"}{" "}
                                  <span className="text-danger">*</span>
                                </Label>
                                {values.employmentTax.p45Upload ? (
                                  <div className="d-flex align-items-center gap-2 flex-wrap">
                                    {renderSelectedFiles(
                                      Array.isArray(
                                        values.employmentTax.p45Upload
                                      )
                                        ? values.employmentTax.p45Upload
                                        : [values.employmentTax.p45Upload]
                                    )}
                                    <Button
                                      type="button"
                                      color="secondary"
                                      outline
                                      onClick={() =>
                                        typeof document !== "undefined" &&
                                        document
                                          .getElementById("p45UploadInput")
                                          ?.click()
                                      }
                                    >
                                      {t("change") || "Change"}
                                    </Button>
                                    <Button
                                      type="button"
                                      color="danger"
                                      outline
                                      onClick={() => {
                                        setNested(
                                          "employmentTax.p45Upload",
                                          null
                                        );
                                        clearFileInput("p45UploadInput");
                                      }}
                                    >
                                      {t("remove") || "Remove"}
                                    </Button>
                                  </div>
                                ) : employmentTaxExistingP45.length ? (
                                  <div className="d-flex align-items-center gap-2 flex-wrap">
                                    {renderStoredFiles(
                                      employmentTaxExistingP45
                                    )}
                                    <Button
                                      type="button"
                                      color="secondary"
                                      outline
                                      onClick={() =>
                                        typeof document !== "undefined" &&
                                        document
                                          .getElementById("p45UploadInput")
                                          ?.click()
                                      }
                                    >
                                      {t("replace") || "Replace"}
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    type="button"
                                    color="secondary"
                                    outline
                                    onClick={() =>
                                      typeof document !== "undefined" &&
                                      document
                                        .getElementById("p45UploadInput")
                                        ?.click()
                                    }
                                  >
                                    {t("upload") || "Upload"}
                                  </Button>
                                )}
                                <input
                                  id="p45UploadInput"
                                  type="file"
                                  accept=".pdf,image/*"
                                  className="d-none"
                                  onChange={(e) =>
                                    handleFileChange(
                                      "employmentTax.p45Upload",
                                      e.currentTarget.files?.[0] || null,
                                      FILE_VALIDATION.MAX_MB
                                    )
                                  }
                                />
                                {touched.employmentTax?.p45Upload &&
                                  errors.employmentTax?.p45Upload && (
                                    <div className="invalid-feedback d-block">
                                      {errors.employmentTax?.p45Upload}
                                    </div>
                                  )}
                              </Col>

                              <Col md={12} className="mb-3">
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-decoration-none"
                                  onClick={() => {
                                    setNested("employmentTax.hasP45", "no");
                                    setNested("employmentTax.p45Upload", null);
                                    clearFileInput("p45UploadInput");
                                  }}
                                  style={{
                                    textAlign: "left",
                                    fontSize: "inherit",
                                  }}
                                >
                                  {t("cantUploadContinueWithoutP45")}
                                </button>
                                <div className="text-muted small mt-1">
                                  {t("clickingThisUnselectsP45")}
                                </div>
                              </Col>
                            </>
                          )}

                          {values.employmentTax.hasP45 === "no" && (
                            <>
                              <Col md={12} className="mb-3">
                                <Label className="fw-bolder d-block">
                                  Select which applies{" "}
                                  <span className="text-danger">*</span>
                                </Label>
                                {STARTER_CHECKLIST_OPTIONS.map((option) => {
                                  const id = `starter-checklist-${option.value}`;
                                  return (
                                    <div
                                      className="form-check mb-2"
                                      key={option.value}
                                    >
                                      <Input
                                        id={id}
                                        className="form-check-input"
                                        type="radio"
                                        name="employmentTax.starterChecklistChoice"
                                        value={option.value}
                                        checked={
                                          values.employmentTax
                                            .starterChecklistChoice ===
                                          option.value
                                        }
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor={id}
                                      >
                                        <span className="d-block">
                                          {option.intro}
                                        </span>
                                        {option.bullets?.length > 0 && (
                                          <ul className="mb-0 ps-3">
                                            {option.bullets.map((item) => (
                                              <li key={item}>{item}</li>
                                            ))}
                                          </ul>
                                        )}
                                      </Label>
                                    </div>
                                  );
                                })}
                                {touched.employmentTax?.starterChecklistChoice &&
                                  errors.employmentTax
                                    ?.starterChecklistChoice && (
                                    <div className="invalid-feedback d-block">
                                      {
                                        errors.employmentTax
                                          ?.starterChecklistChoice
                                      }
                                    </div>
                                  )}
                              </Col>
                              <Col md={12} className="mb-3">
                                <Label className="fw-bolder d-block">
                                  Do you have a student or postgraduate loan?{" "}
                                  <span className="text-danger">*</span>
                                </Label>
                                <div className="d-flex gap-3 flex-wrap">
                                  <div className="form-check">
                                    <Input
                                      id="student-loan-yes"
                                      className="form-check-input"
                                      type="radio"
                                      name="employmentTax.hasPostgraduateLoan"
                                      value="yes"
                                      checked={
                                        values.employmentTax
                                          .hasPostgraduateLoan === "yes"
                                      }
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                    />
                                    <Label
                                      className="form-check-label"
                                      htmlFor="student-loan-yes"
                                    >
                                      {t("yes") || "Yes"}
                                    </Label>
                                  </div>
                                  <div className="form-check">
                                    <Input
                                      id="student-loan-no"
                                      className="form-check-input"
                                      type="radio"
                                      name="employmentTax.hasPostgraduateLoan"
                                      value="no"
                                      checked={
                                        values.employmentTax
                                          .hasPostgraduateLoan === "no"
                                      }
                                      onChange={(e) => {
                                        handleChange(e);
                                        setNested("employmentTax.planType", "");
                                      }}
                                      onBlur={handleBlur}
                                    />
                                    <Label
                                      className="form-check-label"
                                      htmlFor="student-loan-no"
                                    >
                                      {t("no") || "No"}
                                    </Label>
                                  </div>
                                </div>
                                {touched.employmentTax?.hasPostgraduateLoan &&
                                  errors.employmentTax?.hasPostgraduateLoan && (
                                    <div className="invalid-feedback d-block">
                                      {
                                        errors.employmentTax?.hasPostgraduateLoan
                                      }
                                    </div>
                                  )}
                              </Col>
                              {values.employmentTax.hasPostgraduateLoan ===
                                "yes" && (
                                <Col md={6} lg={3}>
                                  <Label className="fw-bolder">
                                    Select the correct student loan plan type or
                                    postgraduate loan{" "}
                                    <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    type="select"
                                    name="employmentTax.planType"
                                    value={values.employmentTax.planType}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    invalid={
                                      touched.employmentTax?.planType &&
                                      !!errors.employmentTax?.planType
                                    }
                                  >
                                    <option value="">
                                      {t("select") || "Select"}
                                    </option>
                                    {STUDENT_LOAN_PLAN_OPTIONS.map((option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                  </Input>
                                  <FormFeedback>
                                    {errors.employmentTax?.planType}
                                  </FormFeedback>
                                </Col>
                              )}
                              <Col md={12}>
                                <Label className="fw-bolder">
                                  {t("notes")}
                                </Label>
                                <Input
                                  type="textarea"
                                  name="employmentTax.notes"
                                  value={values.employmentTax.notes || ""}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  rows={2}
                                />
                              </Col>
                            </>
                          )}

                          <Col md={12} className="mb-3">
                            <div className="form-check">
                              <Input
                                id="acceptEmergency"
                                className="form-check-input"
                                type="checkbox"
                                name="employmentTax.acceptEmergencyTaxCode"
                                checked={
                                  values.employmentTax.acceptEmergencyTaxCode
                                }
                                onChange={setChecked(
                                  "employmentTax.acceptEmergencyTaxCode"
                                )}
                                onBlur={() =>
                                  touchNested(
                                    "employmentTax.acceptEmergencyTaxCode"
                                  )
                                }
                                invalid={
                                  touched.employmentTax
                                    ?.acceptEmergencyTaxCode &&
                                  !!errors.employmentTax?.acceptEmergencyTaxCode
                                }
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="acceptEmergency"
                              >
                                {t("acceptEmergencyTax")}{" "}
                                <span className="text-danger">*</span>
                              </Label>
                            </div>
                            {touched.employmentTax?.acceptEmergencyTaxCode &&
                              errors.employmentTax?.acceptEmergencyTaxCode && (
                                <div className="invalid-feedback d-block">
                                  {
                                    errors.employmentTax?.acceptEmergencyTaxCode
                                  }
                                </div>
                              )}
                          </Col>
                        </Row>
                      </CardBody>
                    </Collapse>
                  </Card>

                  {/* ===== Bank Details ===== */}
                  <Card className="mb-3">
                    <CardHeader
                      onClick={() => setOpenBankDetails((v) => !v)}
                      className="d-flex justify-content-between align-items-center"
                      style={{ cursor: "pointer", backgroundColor: "#eceeee" }}
                    >
                      <h6 className="mb-0 fw-bolder">{t("bankDetails")}</h6>
                      <i
                        className={`mdi mdi-chevron-${
                          openBankDetails ? "up" : "down"
                        } bg-white text-secondary border border-secondary rounded-circle`}
                        style={{
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                        }}
                      ></i>
                    </CardHeader>
                    <Collapse isOpen={openBankDetails}>
                      <CardBody style={{ backgroundColor: "#eceeee" }}>
                        <Row className="gy-3 gx-4">
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">
                              {t("accHolder")}
                            </Label>
                            <Input
                              name="bankDetails.accountHolderName"
                              value={values.bankDetails.accountHolderName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={invalid("bankDetails.accountHolderName")}
                              placeholder={t("placeholderAccHolder")}
                            />
                            <FormFeedback>
                              {errors.bankDetails?.accountHolderName}
                            </FormFeedback>
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">{t("sortCode")}</Label>
                            <Input
                              name="bankDetails.sortCode"
                              value={values.bankDetails.sortCode}
                              onChange={handleSortCodeChange}
                              onBlur={handleBlur}
                              invalid={invalid("bankDetails.sortCode")}
                              placeholder="12-34-56"
                              maxLength={8}
                            />
                            <FormFeedback>
                              {errors.bankDetails?.sortCode}
                            </FormFeedback>
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">
                              {t("accountNumber")}
                            </Label>
                            <Input
                              name="bankDetails.accountNumber"
                              value={values.bankDetails.accountNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={invalid("bankDetails.accountNumber")}
                              placeholder="12345678"
                            />
                            <FormFeedback>
                              {errors.bankDetails?.accountNumber}
                            </FormFeedback>
                          </Col>
                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">{t("bankName")}</Label>
                            <Input
                              name="bankDetails.bankName"
                              value={values.bankDetails.bankName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              invalid={invalid("bankDetails.bankName")}
                              placeholder={t("placeholderBankName")}
                            />
                            <FormFeedback>
                              {errors.bankDetails?.bankName}
                            </FormFeedback>
                          </Col>
                        </Row>
                      </CardBody>
                    </Collapse>
                  </Card>

                  {/* ===== More About You ===== */}
                  <Card className="mb-3">
                    <CardHeader
                      onClick={() => setOpenMoreAboutYou((v) => !v)}
                      className="d-flex justify-content-between align-items-center"
                      style={{ cursor: "pointer", backgroundColor: "#eceeee" }}
                    >
                      <h6 className="mb-0 fw-bolder">
                        {t("moreAboutYouTitle")}
                      </h6>
                      <i
                        className={`mdi mdi-chevron-${
                          openMoreAboutYou ? "up" : "down"
                        } bg-white text-secondary border border-secondary rounded-circle`}
                        style={{
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                        }}
                      ></i>
                    </CardHeader>
                    <Collapse isOpen={openMoreAboutYou}>
                      <CardBody style={{ backgroundColor: "#eceeee" }}>
                        <div className="mb-4">
                          <h6 className="text-dark mb-2">
                            {t("moreAboutYouHealthTitle")}
                          </h6>
                          <p className="text-muted small mb-2">
                            {t("moreAboutYouHealthIntro")}
                          </p>
                          <Input
                            type="textarea"
                            name="moreAboutYou.healthAccessibilityDetails"
                            value={
                              values.moreAboutYou.healthAccessibilityDetails
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            rows={4}
                            placeholder={t("moreAboutYouHealthPlaceholder")}
                            invalid={moreAboutYouFieldShowError(
                              "healthAccessibilityDetails"
                            )}
                          />
                          {moreAboutYouFieldShowError(
                            "healthAccessibilityDetails"
                          ) && (
                            <div className="invalid-feedback d-block">
                              {errors.moreAboutYou?.healthAccessibilityDetails}
                            </div>
                          )}
                        </div>
                        <div className="mb-4">
                          <h6 className="text-dark mb-2">
                            {t("moreAboutYouConvictionsTitle")}
                          </h6>
                          <p className="text-muted small mb-2">
                            {t("moreAboutYouConvictionsIntro")}
                          </p>
                          <div className="form-check mb-2">
                            <Input
                              id="hasNoConvictions"
                              className="form-check-input"
                              type="radio"
                              name="moreAboutYouCriminalConvictions"
                              checked={values.moreAboutYou.hasNoConvictions}
                              onChange={() => setConvictionsChoice("none")}
                              invalid={convictionsRadiosShowError}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="hasNoConvictions"
                            >
                              {t("moreAboutYouConvictionsNone")}
                            </Label>
                          </div>
                          <div className="form-check mb-2">
                            <Input
                              id="hasConvictionsOrPending"
                              className="form-check-input"
                              type="radio"
                              name="moreAboutYouCriminalConvictions"
                              checked={
                                values.moreAboutYou.hasConvictionsOrPending
                              }
                              onChange={() => setConvictionsChoice("has")}
                              invalid={convictionsRadiosShowError}
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="hasConvictionsOrPending"
                            >
                              {t("moreAboutYouConvictionsHas")}
                            </Label>
                          </div>
                          {values.moreAboutYou.hasConvictionsOrPending && (
                            <Input
                              type="textarea"
                              name="moreAboutYou.criminalConvictionsDetails"
                              value={
                                values.moreAboutYou.criminalConvictionsDetails
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              rows={4}
                              placeholder={t(
                                "moreAboutYouConvictionsDetailsPlaceholder"
                              )}
                              className="mt-2"
                              invalid={moreAboutYouFieldShowError(
                                "criminalConvictionsDetails"
                              )}
                            />
                          )}
                          {moreAboutYouFieldShowError(
                            "criminalConvictionsDetails"
                          ) && (
                            <div className="invalid-feedback d-block">
                              {errors.moreAboutYou?.criminalConvictionsDetails}
                            </div>
                          )}
                          {convictionsRadiosShowError && (
                            <div className="invalid-feedback d-block">
                              {convictionsChoiceError}
                            </div>
                          )}
                        </div>
                        <div>
                          <h6 className="text-dark mb-2">
                            {t("moreAboutYouWorkHistoryTitle")}
                          </h6>
                          <p className="text-muted small mb-2">
                            {t("moreAboutYouWorkHistoryIntro")}
                          </p>
                          <Input
                            type="textarea"
                            name="moreAboutYou.aboutYouWorkHistory"
                            value={values.moreAboutYou.aboutYouWorkHistory}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            rows={4}
                            placeholder={t(
                              "moreAboutYouWorkHistoryPlaceholder"
                            )}
                            invalid={moreAboutYouFieldShowError(
                              "aboutYouWorkHistory"
                            )}
                          />
                          {moreAboutYouFieldShowError(
                            "aboutYouWorkHistory"
                          ) && (
                            <div className="invalid-feedback d-block">
                              {errors.moreAboutYou?.aboutYouWorkHistory}
                            </div>
                          )}
                        </div>
                      </CardBody>
                    </Collapse>
                  </Card>

                  {/* ===== Work Preferences ===== */}
                  <Card className="mb-3">
                    <CardHeader
                      onClick={() => setOpenWorkPreferences((v) => !v)}
                      className="d-flex justify-content-between align-items-center"
                      style={{ cursor: "pointer", backgroundColor: "#eceeee" }}
                    >
                      <h6 className="mb-0 fw-bolder">{t("workPreferences")}</h6>
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          size="sm"
                          color="outline-primary"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            addSector();
                          }}
                        >
                          {t("addSector") || "+ Add Sector"}
                        </Button>
                        <i
                          className={`mdi mdi-chevron-${
                            openWorkPreferences ? "up" : "down"
                          } bg-white text-secondary border border-secondary rounded-circle`}
                          style={{
                            width: "20px",
                            height: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                          }}
                        ></i>
                      </div>
                    </CardHeader>
                    <Collapse isOpen={openWorkPreferences}>
                      <CardBody style={{ backgroundColor: "#eceeee" }}>
                        <Row className="gy-3 gx-4">
                          <Col md={12}>
                            {(values.workPreferences.sectors || []).map(
                              (sec, idx) => {
                                const touchedRow =
                                  touched.workPreferences?.sectors?.[idx] || {};
                                const errorsRow =
                                  errors.workPreferences?.sectors?.[idx] || {};
                                const sectorSlug = sec.sectorSlug || "";
                                const roleOptions = getRoleOptions(sectorSlug);
                                const selectedRoles = sec.roles || [];

                                return (
                                  <Card className="mb-3" key={idx}>
                                    <CardBody>
                                      <Row>
                                        <Col md={5} className="mb-3">
                                          <Label>
                                            {t("sector") || "Sector"}
                                          </Label>
                                          <Input
                                            type="select"
                                            value={sectorSlug}
                                            onChange={(e) =>
                                              setSectorSlug(idx, e.target.value)
                                            }
                                            onBlur={() =>
                                              touchNested(
                                                `workPreferences.sectors[${idx}].sectorSlug`
                                              )
                                            }
                                            disabled={sectorsLoading}
                                            className={
                                              touchedRow?.sectorSlug &&
                                              errorsRow?.sectorSlug
                                                ? "is-invalid"
                                                : ""
                                            }
                                          >
                                            <option value="">
                                              {sectorsLoading
                                                ? t("loading") || "Loading..."
                                                : t("select")}
                                            </option>
                                            {getSectorOptionsForRow(idx).map(
                                              (opt) => (
                                                <option
                                                  key={opt.value}
                                                  value={opt.value}
                                                  disabled={opt.disabled}
                                                >
                                                  {opt.label}
                                                </option>
                                              )
                                            )}
                                          </Input>
                                          {touchedRow?.sectorSlug &&
                                            errorsRow?.sectorSlug && (
                                              <div className="invalid-feedback d-block">
                                                {errorsRow?.sectorSlug}
                                              </div>
                                            )}
                                        </Col>

                                        <Col md={7} className="mb-3">
                                          <Label>{t("roles") || "Roles"}</Label>
                                          <Select
                                            isMulti
                                            isDisabled={
                                              !sectorSlug ||
                                              roleOptions.length === 0
                                            }
                                            options={roleOptions}
                                            value={roleOptions.filter((o) =>
                                              (selectedRoles || []).includes(
                                                o.value
                                              )
                                            )}
                                            onChange={(opts) => {
                                              const vals = Array.isArray(opts)
                                                ? opts.map((o) => o.value)
                                                : [];
                                              setRolesForSector(idx, vals);
                                            }}
                                            onBlur={() =>
                                              touchNested(
                                                `workPreferences.sectors[${idx}].roles`
                                              )
                                            }
                                            placeholder={
                                              !sectorSlug
                                                ? t("selectSectorFirst") ||
                                                  "Select a sector first"
                                                : t("chooseRoles") ||
                                                  "Choose roles..."
                                            }
                                            classNamePrefix="rs"
                                            menuPortalTarget={
                                              typeof document !== "undefined"
                                                ? document.body
                                                : null
                                            }
                                            styles={{
                                              menuPortal: (base) => ({
                                                ...base,
                                                zIndex: 9999,
                                              }),
                                              menu: (base) => ({
                                                ...base,
                                                backgroundColor: "#fff",
                                                zIndex: 9999,
                                              }),
                                              control: (base) => ({
                                                ...base,
                                                minHeight: 44,
                                                backgroundColor: "#fff",
                                                borderColor:
                                                  touchedRow?.roles &&
                                                  errorsRow?.roles
                                                    ? "#dc3545"
                                                    : base.borderColor,
                                                boxShadow:
                                                  touchedRow?.roles &&
                                                  errorsRow?.roles
                                                    ? "0 0 0 0.2rem rgba(220,53,69,.25)"
                                                    : base.boxShadow,
                                              }),
                                            }}
                                          />
                                          {touchedRow?.roles &&
                                            errorsRow?.roles && (
                                              <div className="invalid-feedback d-block">
                                                {typeof errorsRow?.roles ===
                                                "string"
                                                  ? errorsRow?.roles
                                                  : t("required") || "Required"}
                                              </div>
                                            )}
                                        </Col>

                                        <Col
                                          md={12}
                                          className="d-flex justify-content-end"
                                        >
                                          {idx > 0 && (
                                            <Button
                                              color="outline-danger"
                                              size="sm"
                                              type="button"
                                              onClick={() => removeSector(idx)}
                                            >
                                              {t("removeSector") ||
                                                "Remove Sector"}
                                            </Button>
                                          )}
                                        </Col>
                                      </Row>
                                    </CardBody>
                                  </Card>
                                );
                              }
                            )}
                          </Col>

                          <Col md={6} lg={3}>
                            <Label className="fw-bolder">
                              {t("preferredRadius")}
                            </Label>
                            <Input
                              type="select"
                              name="workPreferences.preferredWorkRadius"
                              value={
                                values.workPreferences.preferredWorkRadius ===
                                  "" ||
                                values.workPreferences.preferredWorkRadius ==
                                  null
                                  ? ""
                                  : String(
                                      values.workPreferences.preferredWorkRadius
                                    )
                              }
                              onChange={(e) => {
                                const v = e.target.value;
                                setNested(
                                  "workPreferences.preferredWorkRadius",
                                  v === "" ? "" : Number(v)
                                );
                              }}
                              onBlur={handleBlur}
                              invalid={invalid(
                                "workPreferences.preferredWorkRadius"
                              )}
                            >
                              <option value="">{t("select")}</option>
                              {PREFERRED_WORK_RADIUS_OPTIONS.map((radius) => (
                                <option key={radius} value={radius}>
                                  {radius}
                                </option>
                              ))}
                            </Input>
                            <FormFeedback>
                              {errors.workPreferences?.preferredWorkRadius}
                            </FormFeedback>
                          </Col>

                          <Col md={12} lg={6}>
                            <Label className="fw-bolder">
                              {t("availability")}
                            </Label>
                            <div className="d-flex gap-3 flex-wrap">
                              {AVAILABILITY_OPTIONS.map((opt) => (
                                <div className="form-check" key={opt}>
                                  <Input
                                    id={`avail_${opt}`}
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={values.workPreferences.availability.includes(
                                      opt
                                    )}
                                    onChange={() =>
                                      toggleInArray(
                                        "workPreferences.availability",
                                        opt
                                      )
                                    }
                                  />
                                  <Label
                                    className="form-check-label"
                                    htmlFor={`avail_${opt}`}
                                  >
                                    {t(opt)}
                                  </Label>
                                </div>
                              ))}
                            </div>
                            {touched.workPreferences?.availability &&
                              errors.workPreferences?.availability && (
                                <div className="invalid-feedback d-block">
                                  {errors.workPreferences?.availability}
                                </div>
                              )}
                          </Col>

                          <Col md={12}>
                            <div className="form-check">
                              <Input
                                id="optOut48"
                                className="form-check-input"
                                type="checkbox"
                                name="workPreferences.optOut48HourLimit"
                                checked={
                                  !!values.workPreferences.optOut48HourLimit
                                }
                                onChange={setChecked(
                                  "workPreferences.optOut48HourLimit"
                                )}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="optOut48"
                              >
                                {t("optOut48")}
                              </Label>
                            </div>
                          </Col>

                          <Col md={12}>
                            <Label className="fw-bolder">
                              {t("whatMattersMost")}
                            </Label>
                            <div className="d-flex gap-3 flex-wrap">
                              {WHAT_MATTERS_MOST_OPTIONS.map((opt) => (
                                <div className="form-check" key={opt}>
                                  <Input
                                    id={`whatMatters_${opt.replace(
                                      /\s+/g,
                                      "_"
                                    )}`}
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={values.workPreferences.whatMattersMost.includes(
                                      opt
                                    )}
                                    onChange={() => toggleWhatMattersMost(opt)}
                                  />
                                  <Label
                                    className="form-check-label"
                                    htmlFor={`whatMatters_${opt.replace(
                                      /\s+/g,
                                      "_"
                                    )}`}
                                  >
                                    {opt}
                                  </Label>
                                </div>
                              ))}
                            </div>
                            {touched.workPreferences?.whatMattersMost &&
                              errors.workPreferences?.whatMattersMost && (
                                <div className="invalid-feedback d-block">
                                  {Array.isArray(
                                    errors.workPreferences.whatMattersMost
                                  )
                                    ? errors.workPreferences.whatMattersMost
                                        .filter(Boolean)
                                        .join(" ")
                                    : errors.workPreferences.whatMattersMost}
                                </div>
                              )}
                          </Col>

                          <Col md={12} lg={6}>
                            <Label className="fw-bolder">
                              {t("transport")}
                            </Label>
                            <div className="d-flex gap-3 flex-wrap">
                              {TRANSPORT_OPTIONS.map((tr) => (
                                <div className="form-check" key={tr}>
                                  <Input
                                    id={`tr_${tr}`}
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={values.workPreferences.transport.includes(
                                      tr
                                    )}
                                    onChange={() =>
                                      toggleInArray(
                                        "workPreferences.transport",
                                        tr
                                      )
                                    }
                                  />
                                  <Label
                                    className="form-check-label"
                                    htmlFor={`tr_${tr}`}
                                  >
                                    {t(tr)}
                                  </Label>
                                </div>
                              ))}
                            </div>
                            {touched.workPreferences?.transport &&
                              errors.workPreferences?.transport && (
                                <div className="invalid-feedback d-block">
                                  {errors.workPreferences?.transport}
                                </div>
                              )}
                          </Col>
                        </Row>
                      </CardBody>
                    </Collapse>
                  </Card>

                  {/* ===== Certificates and compliance ===== */}
                  <Card className="mb-3">
                    <CardHeader
                      onClick={() => setOpenCompliance((v) => !v)}
                      className="d-flex justify-content-between align-items-center"
                      style={{ cursor: "pointer", backgroundColor: "#eceeee" }}
                    >
                      <h6 className="mb-0 fw-bolder">
                        {t("certificatesAndCompliance")}
                      </h6>
                      <i
                        className={`mdi mdi-chevron-${
                          openCompliance ? "up" : "down"
                        } bg-white text-secondary border border-secondary rounded-circle`}
                        style={{
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                        }}
                      ></i>
                    </CardHeader>
                    <Collapse isOpen={openCompliance}>
                      <CardBody style={{ backgroundColor: "#eceeee" }}>
                        <p className="mb-3">
                          {t("certificatesAndComplianceIntro")}
                        </p>
                        <div className="mb-4">
                          <h6 className="text-dark mb-1">
                            {t("certificatesDrivingDocsTitle")}
                          </h6>
                          <p className="text-muted small mb-3">
                            {t("certificatesDrivingDocsIntro")}
                          </p>
                          <Row className="gy-3 gx-4">
                            <Col md={6} lg={3}>
                              <Label className="fw-bolder">
                                {t("drivingLicenseUpload")}
                              </Label>
                              {values.rightToWork.drivingLicense
                                ? renderSelectedFiles([
                                    values.rightToWork.drivingLicense,
                                  ])
                                : renderStoredFiles(
                                    values.rightToWork.drivingLicenseUrl
                                      ? [values.rightToWork.drivingLicenseUrl]
                                      : []
                                  )}
                              <Input
                                type="file"
                                accept=".pdf,image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "rightToWork.drivingLicense",
                                    e.currentTarget.files?.[0] || null,
                                    FILE_VALIDATION.MAX_MB
                                  )
                                }
                                onBlur={() =>
                                  touchNested("rightToWork.drivingLicense")
                                }
                                invalid={invalid("rightToWork.drivingLicense")}
                              />
                              {touched.rightToWork?.drivingLicense &&
                                errors.rightToWork?.drivingLicense && (
                                  <div className="invalid-feedback d-block">
                                    {errors.rightToWork.drivingLicense}
                                  </div>
                                )}
                            </Col>
                            <Col md={6} lg={3}>
                              <Label className="fw-bolder">
                                {t("drivingLicenseBack") || "Driving License Back"}
                              </Label>
                              {values.rightToWork.drivingLicenseBack
                                ? renderSelectedFiles([
                                    values.rightToWork.drivingLicenseBack,
                                  ])
                                : renderStoredFiles(
                                    values.rightToWork.drivingLicenseBackUrl
                                      ? [
                                          values.rightToWork
                                            .drivingLicenseBackUrl,
                                        ]
                                      : []
                                  )}
                              <Input
                                type="file"
                                accept=".pdf,image/*"
                                onChange={(e) =>
                                  handleFileChange(
                                    "rightToWork.drivingLicenseBack",
                                    e.currentTarget.files?.[0] || null,
                                    FILE_VALIDATION.MAX_MB
                                  )
                                }
                                onBlur={() =>
                                  touchNested("rightToWork.drivingLicenseBack")
                                }
                                invalid={invalid(
                                  "rightToWork.drivingLicenseBack"
                                )}
                              />
                              {touched.rightToWork?.drivingLicenseBack &&
                                errors.rightToWork?.drivingLicenseBack && (
                                  <div className="invalid-feedback d-block">
                                    {errors.rightToWork.drivingLicenseBack}
                                  </div>
                                )}
                            </Col>
                            <Col md={6} lg={3}>
                              <Label className="fw-bolder">
                                {t("dvlaShareCode")}
                              </Label>
                              <Input
                                name="rightToWork.dvlaShareCode"
                                value={values.rightToWork.dvlaShareCode}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                invalid={invalid("rightToWork.dvlaShareCode")}
                                placeholder={t("placeholderDvlaShareCode")}
                              />
                              <FormFeedback>
                                {errors.rightToWork?.dvlaShareCode}
                              </FormFeedback>
                              <small className="text-muted d-block mt-1">
                                {t("dvlaShareCodeHelperPrefix")}{" "}
                                <a
                                  href={UK_DVLA_SHARE_CODE_URL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {t("shareCodeHelperLinkText")}
                                </a>
                                .
                              </small>
                            </Col>
                            <Col md={6} lg={3}>
                              <Label className="fw-bolder">
                                {t("drivingLicenseExpiryDate")}
                              </Label>
                              <Input
                                type="date"
                                name="rightToWork.drivingLicenseExpiryDate"
                                min={new Date().toISOString().split("T")[0]}
                                value={
                                  values.rightToWork.drivingLicenseExpiryDate ||
                                  ""
                                }
                                onChange={handleChange}
                                onBlur={handleBlur}
                                invalid={invalid(
                                  "rightToWork.drivingLicenseExpiryDate"
                                )}
                              />
                              <FormFeedback>
                                {errors.rightToWork?.drivingLicenseExpiryDate}
                              </FormFeedback>
                            </Col>
                            <Col md={6} lg={3}>
                              <Label className="fw-bolder">
                                {t("licenseNumber")}
                              </Label>
                              <Input
                                name="rightToWork.licenseNumber"
                                value={values.rightToWork.licenseNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                invalid={invalid("rightToWork.licenseNumber")}
                                placeholder={t("placeholderLicenseNumber")}
                              />
                              <FormFeedback>
                                {errors.rightToWork?.licenseNumber}
                              </FormFeedback>
                            </Col>
                          </Row>
                          <div className="mt-3">
                            <AdminReviewPanel
                              title={t("adminReviewTitle")}
                              description={t("adminReviewDrivingDescription")}
                            >
                              <Col md={6} lg={4}>
                                <AdminReviewMetaRow
                                  label={t("drivingLicenseUpload")}
                                  status={
                                    values.rightToWork.drivingLicenseStatus
                                  }
                                  t={t}
                                />
                              </Col>
                            </AdminReviewPanel>
                          </div>
                        </div>
                        {certsLoading ? (
                          <div className="text-center py-4">
                            <span className="spinner-border spinner-border-sm me-2" />
                            {t("loading")}
                          </div>
                        ) : (
                          <>
                            {typeof errors.certificateEntries === "string" && (
                              <div className="invalid-feedback d-block mb-3">
                                {errors.certificateEntries}
                              </div>
                            )}
                            <Row>
                              {(values.certificateEntries || []).map(
                                (entry, idx) => {
                                  const certOptions = (certsData || []).map(
                                    (cert) => ({
                                      value: cert.slug,
                                      label: toSentenceCase(cert.title),
                                    })
                                  );
                                  const selectedCert = certsData?.find(
                                    (c) => c.slug === entry?.certSlug
                                  );
                                  const isDbs = selectedCert?.hasServiceNumber;
                                  const hasCertFile = !!(
                                    entry?.file &&
                                    (entry.file instanceof File ||
                                      entry.file?.name)
                                  );
                                  const certTypeColMd = isDbs ? 3 : 5;
                                  const fileColMd = isDbs ? 4 : 5;
                                  const serviceNumberColMd = 4;
                                  const removeColMd = isDbs ? 1 : 2;
                                  const entryError =
                                    errors.certificateEntries?.[idx];
                                  const entryTouched =
                                    touched.certificateEntries?.[idx];

                                  return (
                                    <Col
                                      md={12}
                                      key={entry?.id ?? idx}
                                      className="mb-3"
                                    >
                                      <div
                                        className="p-3 rounded border"
                                        style={{ backgroundColor: "#f9f9f9" }}
                                      >
                                        <Row className="align-items-start">
                                          <Col
                                            md={certTypeColMd}
                                            className="mb-2"
                                          >
                                            <Label className="fw-bolder mb-1">
                                              {t("certificateTypeLabel")}
                                            </Label>
                                            <Select
                                              options={certOptions}
                                              value={
                                                certOptions.find(
                                                  (o) =>
                                                    o.value === entry?.certSlug
                                                ) || null
                                              }
                                              onChange={(opt) => {
                                                const list = [
                                                  ...(values.certificateEntries ||
                                                    []),
                                                ];
                                                if (!list[idx]) return;
                                                list[idx] = {
                                                  ...list[idx],
                                                  certSlug: opt?.value ?? "",
                                                  serviceNumber: "",
                                                  file: null,
                                                };
                                                setFieldValue(
                                                  "certificateEntries",
                                                  list
                                                );
                                              }}
                                              onBlur={() => {
                                                const list =
                                                  values.certificateEntries ||
                                                  [];
                                                const arr = Array.isArray(
                                                  touched.certificateEntries
                                                )
                                                  ? [
                                                      ...touched.certificateEntries,
                                                    ]
                                                  : [];
                                                while (arr.length < list.length)
                                                  arr.push(false);
                                                arr[idx] = true;
                                                setFieldTouched(
                                                  "certificateEntries",
                                                  arr
                                                );
                                              }}
                                              isClearable
                                              placeholder={t("select")}
                                              classNamePrefix="rs"
                                              menuPortalTarget={
                                                typeof document !== "undefined"
                                                  ? document.body
                                                  : null
                                              }
                                            />
                                          </Col>
                                          <Col md={fileColMd} className="mb-2">
                                            <Label className="fw-bolder mb-1">
                                              {t("uploadFile")}
                                            </Label>
                                            <Input
                                              type="file"
                                              accept=".pdf,image/*"
                                              innerRef={(el) => {
                                                const id = entry?.id;
                                                if (id == null) return;
                                                if (el)
                                                  certFileInputRefsRef.current[
                                                    id
                                                  ] = el;
                                                else
                                                  delete certFileInputRefsRef
                                                    .current[id];
                                              }}
                                              onChange={(e) => {
                                                const file =
                                                  e.target.files?.[0] ?? null;
                                                handleCertificateFile(
                                                  idx,
                                                  file,
                                                  e.target
                                                );
                                              }}
                                            />
                                            <div
                                              className="mt-1"
                                              style={{ minHeight: "20px" }}
                                            >
                                              {hasCertFile ? (
                                                <div className="d-flex align-items-start gap-2 flex-wrap">
                                                  {renderSelectedFiles([
                                                    entry.file,
                                                  ])}
                                                  <Button
                                                    type="button"
                                                    color="link"
                                                    className="p-0 lh-1 text-danger flex-shrink-0"
                                                    onClick={() =>
                                                      clearCertificateEntryFile(
                                                        idx,
                                                        entry?.id
                                                      )
                                                    }
                                                  >
                                                    <i className="mdi mdi-close fs-5" />
                                                  </Button>
                                                </div>
                                              ) : (
                                                renderStoredFiles(
                                                  entry.existingFiles
                                                )
                                              )}
                                            </div>
                                          </Col>
                                          {isDbs && (
                                            <Col
                                              md={serviceNumberColMd}
                                              className="mb-2"
                                            >
                                              <Label className="fw-bolder mb-1">
                                                {t("serviceNumber")}
                                              </Label>
                                              <Input
                                                type="text"
                                                value={
                                                  entry?.serviceNumber ?? ""
                                                }
                                                onChange={(e) =>
                                                  handleDbsCertificateServiceNumberChange(
                                                    idx,
                                                    entry?.id,
                                                    e.target.value
                                                  )
                                                }
                                                placeholder="e.g. C1234567890"
                                              />
                                              <small className="text-muted d-block mt-1">
                                                {t(
                                                  "dbsServiceNumberHelperPrefix"
                                                )}{" "}
                                                <a
                                                  href={
                                                    UK_DBS_UPDATE_SERVICE_URL
                                                  }
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                >
                                                  {t(
                                                    "dbsUpdateServiceCheckerLinkText"
                                                  )}
                                                </a>
                                                .
                                              </small>
                                            </Col>
                                          )}
                                          <Col
                                            md={removeColMd}
                                            className="mb-2 d-flex align-items-end flex-column"
                                          >
                                            {(values.certificateEntries
                                              ?.length || 0) > 1 && (
                                              <Button
                                                type="button"
                                                color="outline-danger"
                                                size="sm"
                                                className="align-self-end"
                                                style={{ minHeight: "38px" }}
                                                onClick={() =>
                                                  removeCertificateEntry(idx)
                                                }
                                                title={t("remove")}
                                              >
                                                <i className="mdi mdi-minus" />
                                              </Button>
                                            )}
                                          </Col>
                                        </Row>
                                        {entry?.certSlug &&
                                          (entry?.existingFiles?.length ||
                                            entry?.serviceNumber ||
                                            entry?.adminStatus === true ||
                                            entry?.adminStatus === false ||
                                            entry?.adminExpiryDate ||
                                            entry?.adminIssueDate ||
                                            entry?.adminLastCheckedDate) && (
                                            <div className="mt-3 pt-3 border-top">
                                              <AdminReviewMetaRow
                                                label={
                                                  selectedCert?.title
                                                    ? toSentenceCase(
                                                        selectedCert.title
                                                      )
                                                    : t("certificateTypeLabel")
                                                }
                                                status={entry.adminStatus}
                                                expiryDate={
                                                  entry.adminExpiryDate
                                                }
                                                issueDate={entry.adminIssueDate}
                                                lastCheckedDate={
                                                  entry.adminLastCheckedDate
                                                }
                                                t={t}
                                              />
                                            </div>
                                          )}
                                        {entryTouched && entryError && (
                                          <div className="invalid-feedback d-block mt-1">
                                            {typeof entryError === "object" &&
                                            entryError?.message
                                              ? entryError.message
                                              : entryError}
                                          </div>
                                        )}
                                      </div>
                                    </Col>
                                  );
                                }
                              )}
                            </Row>
                            <Button
                              type="button"
                              color="secondary"
                              size="sm"
                              outline
                              onClick={addCertificateEntry}
                            >
                              {t("addAnother")}
                            </Button>
                          </>
                        )}
                      </CardBody>
                    </Collapse>
                  </Card>

                  <div className="d-grid">
                    <button
                      className="btn btn-primary btn-block"
                      type="submit"
                      disabled={
                        userMe.isPending ||
                        updateUser.isPending ||
                        isSubmitting ||
                        sectorsLoading
                      }
                    >
                      {updateUser.isPending || isSubmitting
                        ? t("saving") || "Saving..."
                        : t("saveChanges") || "Save Changes"}
                    </button>
                  </div>
                </RSForm>
              </>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

// ----- tiny helpers -----
function getAt(obj, path) {
  return pathToArr(path).reduce(
    (o, k) => (o && Object.prototype.hasOwnProperty.call(o, k) ? o[k] : undefined),
    obj
  );
}
function pathToArr(p) {
  return p
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .filter(Boolean);
}
function formatSort(s) {
  const d = (s || "").replace(/\D/g, "").slice(0, 6);
  return d.length === 6 ? d.replace(/(\d{2})(\d{2})(\d{2})/, "$1-$2-$3") : d;
}

export default withRouter(withTranslation()(WorkerUpdate));
